import RecentDocumentHistoryDropDown from 'components/shared/RecentDocumentDropDown';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Search, X } from 'lucide-react';
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchDocument } from 'domain/documents/schemas/document';

interface SearchBarProps {
  minSearchLength: number;
  draftQuery: string;
  onDraftChange: (value: string) => void;
  onCommit: (query: string) => void;

  suggestions: SearchDocument[];
  isSuggestionsLoading: boolean;
}

export default function SearchBarHeader({
  minSearchLength,
  draftQuery,
  onDraftChange,
  onCommit,
  suggestions,
  isSuggestionsLoading,
}: SearchBarProps) {
  const { t } = useTranslation('searchPage');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setIsDropdownOpen(false);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsDropdownOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onDraftChange(e.target.value);
    setIsDropdownOpen(true);
  };

  const handleClear = () => {
    onDraftChange('');
    setIsDropdownOpen(false);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = draftQuery.trim();
    if (trimmed.length >= minSearchLength) {
      onCommit(trimmed);
      setIsDropdownOpen(false);
    }
  };

  const showClearButton = draftQuery.length > 0;

  return (
    <div ref={wrapperRef} className="relative w-full">
      <form onSubmit={handleSubmit} className="flex w-full items-center gap-3">
        <div className="relative flex-1">
          <Input
            className="h-12 w-full rounded-lg border-2 border-primary bg-white pr-11 text-sm outline-hidden transition-all duration-200 focus-visible:border-transparent focus-visible:ring-secondaryYellow"
            value={draftQuery}
            onChange={handleInputChange}
            onFocus={() => setIsDropdownOpen(true)}
            placeholder={t('searchPlaceholder')}
            aria-label={t('ariaSearchInput')}
            autoComplete="off"
          />

          <div className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center gap-1">
            {showClearButton && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClear}
                className="h-8 w-8 rounded-3xl transition hover:bg-gray-100 active:scale-95"
                aria-label={t('ariaClearSearch')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <Button
          type="submit"
          variant="yellowOutline"
          className="flex h-12 items-center justify-center gap-2 rounded-full px-5 transition active:scale-[0.98] disabled:opacity-60 xl:px-6"
          aria-label={t('ariaStartSearch')}
        >
          <Search size={20} />
          <span className="hidden xl:inline-block">{t('searchButton')}</span>
        </Button>
      </form>
      <RecentDocumentHistoryDropDown
        isOpen={isDropdownOpen}
        setIsOpen={setIsDropdownOpen}
        query={draftQuery}
        minSearchLength={minSearchLength}
        suggestions={suggestions}
        isSuggestionsLoading={isSuggestionsLoading}
        onSelectSuggestion={doc => {
          onDraftChange(doc.title ?? draftQuery);
          onCommit(doc.title ?? draftQuery);
          setIsDropdownOpen(false);
        }}
      />
    </div>
  );
}
